<template>
  <div class="student-email-report">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">{{ $t("Book List Category Report") }}</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ reportItems.length }} {{ $t("Categories") }}
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>{{ $t("Print Report") }}</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>{{ $t("Close") }}</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">
        {{ $t("Loading category report...") }}
      </p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadReportData">{{
        $t("Retry")
      }}</b-button>
    </div>

    <div v-else class="report-page">
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div v-if="schoolLogo" class="logo-wrapper mr-3">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">{{
                $t("Book List Category Report")
              }}</span>
              <span class="date-range-text">
                <strong>{{ $t("Generated on") }} {{ currentDateTime }}</strong>
              </span>
            </div>
            <div v-if="filterSummary" class="text-muted mt-50 small">
              {{ filterSummary }}
            </div>
          </div>
        </div>
      </div>

      <div class="summary-section mb-3">
        <div class="summary-card">
          <span class="label">{{ $t("Categories") }}</span>
          <span class="value text-primary">{{ reportItems.length }}</span>
        </div>
        <div class="summary-card">
          <span class="label">{{ $t("Total Required") }}</span>
          <span class="value">{{ totals.required }}</span>
        </div>
        <div class="summary-card">
          <span class="label">{{ $t("Total Received") }}</span>
          <span class="value text-success">{{ totals.received }}</span>
        </div>
        <div class="summary-card">
          <span class="label">{{ $t("Remaining") }}</span>
          <span class="value text-warning">{{ totals.remaining }}</span>
        </div>
        <div class="summary-card">
          <span class="label">{{ $t("Pending Parent Confirmation") }}</span>
          <span class="value text-info">{{ totals.parentPending }}</span>
        </div>
      </div>

      <div class="table-container">
        <table class="ledger-style-table">
          <thead>
            <tr>
              <th class="text-center" style="width: 6%">#</th>
              <th class="text-left" style="width: 34%">CATEGORY</th>
              <th class="text-center" style="width: 15%">REQUIRED</th>
              <th class="text-center" style="width: 15%">RECEIVED</th>
              <th class="text-center" style="width: 15%">REMAINING</th>
              <th class="text-center" style="width: 18%">
                PENDING PARENT CONFIRM
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in reportItems"
              :key="item.categoryID || index"
            >
              <td class="text-center font-weight-bold">{{ index + 1 }}</td>
              <td class="text-left font-weight-bold">
                {{ item.categoryName }}
              </td>
              <td class="text-center">{{ item.requiredQty }}</td>
              <td class="text-center">{{ item.receivedQty }}</td>
              <td class="text-center">{{ item.remainingQty }}</td>
              <td class="text-center">{{ item.parentPendingQty }}</td>
            </tr>
            <tr v-if="!reportItems.length">
              <td colspan="6" class="text-center py-5 no-data">
                <feather-icon
                  icon="InboxIcon"
                  size="32"
                  class="text-muted mb-2"
                />
                <p>{{ $t("No records found for the selected criteria") }}</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "BookListCategoryPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      db: "",
      cID: "",
      classID: "",
      sectionID: "",
      sID: "",
    };
  },
  computed: {
    currentDateTime() {
      const now = new Date();
      return now.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    totals() {
      let required = 0;
      let received = 0;
      let remaining = 0;
      let parentPending = 0;
      this.reportItems.forEach((item) => {
        required += Number(item.requiredQty) || 0;
        received += Number(item.receivedQty) || 0;
        remaining += Number(item.remainingQty) || 0;
        parentPending += Number(item.parentPendingQty) || 0;
      });
      return { required, received, remaining, parentPending };
    },
    filterSummary() {
      const parts = [];
      if (this.classID) parts.push(`${this.$t("Class")}: ${this.classID}`);
      if (this.sectionID)
        parts.push(`${this.$t("Section")}: ${this.sectionID}`);
      if (this.sID) parts.push(`${this.$t("Student")}: ${this.sID}`);
      return parts.join(" · ");
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = query.cID || this.$store.state.userData.cId || 0;
      this.classID = query.classID || "";
      this.sectionID = query.sectionID || "";
      this.sID = query.sID || "";

      if (!this.db) {
        this.error = this.$t(
          "Database identifier is missing. Please close and try again."
        );
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const url = "https://apiv26.myskool.app/api/campuses";
        const response = await axios.get(url, {
          params: { db: this.db, cId: this.cID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data?.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.campusName = match.name;
            this.campusBranch = this.cID == 0 ? "All Campuses" : match.branch;
            this.schoolLogo = match.logo;
          }
        }
      } catch (e) {
        const branches = this.$store.state.branches || [];
        const currentBranch =
          branches.find((b) => b.id == this.cID) ||
          this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.branch ||
            "MY SKOOL";
          this.campusBranch = currentBranch.branch || "";
        }
      }
    },
    mapCategoryRow(item) {
      const requiredQty = Number(
        item.totalRequired != null ? item.totalRequired : item.requiredQty || 0
      );
      const receivedQty = Number(
        item.totalReceived != null ? item.totalReceived : item.receivedQty || 0
      );
      const remainingQty = Number(
        item.totalRemaining != null
          ? item.totalRemaining
          : item.remainingQty != null
          ? item.remainingQty
          : Math.max(requiredQty - receivedQty, 0)
      );
      const parentPendingQty = Number(
        item.totalParentPending != null
          ? item.totalParentPending
          : item.parentPendingQty != null
          ? item.parentPendingQty
          : 0
      );

      return {
        categoryID: item.categoryID || 0,
        categoryName: item.categoryName || item.name || item.category || "—",
        requiredQty,
        receivedQty,
        remainingQty,
        parentPendingQty,
      };
    },
    normalizeReportData(responseData) {
      if (!responseData) return [];
      if (Array.isArray(responseData)) {
        return responseData.map((row) => this.mapCategoryRow(row));
      }
      if (responseData.data && Array.isArray(responseData.data)) {
        return responseData.data.map((row) => this.mapCategoryRow(row));
      }
      if (Array.isArray(responseData.categories)) {
        return responseData.categories.map((row) => this.mapCategoryRow(row));
      }
      return [];
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      try {
        const domain = (
          this.$store.state.domain || "https://apiv26.myskool.app/api/"
        ).replace(/\/?$/, "/");
        let url =
          domain +
          "BookListRecord/ReportByCategory?db=" +
          encodeURIComponent(this.db) +
          "&campusID=" +
          this.cID;
        if (this.classID) url += "&classID=" + this.classID;
        if (this.sectionID != null && this.sectionID !== "") {
          url += "&sectionID=" + this.sectionID;
        }
        if (this.sID) url += "&sID=" + this.sID;

        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (response.data && response.data.status === "success") {
          this.reportItems = this.normalizeReportData(response.data.data);
        } else if (Array.isArray(response.data)) {
          this.reportItems = this.normalizeReportData(response.data);
        } else if (response.data && Array.isArray(response.data.data)) {
          this.reportItems = this.normalizeReportData(response.data.data);
        } else {
          this.error =
            (response.data && response.data.message) ||
            this.$t(
              "Failed to retrieve report data. Please ensure the API endpoint is accessible."
            );
        }
      } catch (err) {
        console.error("Error fetching book list category report:", err);
        this.error = this.$t(
          "Failed to retrieve report data. Please ensure the API endpoint is accessible."
        );
      } finally {
        this.loading = false;
      }
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.student-email-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-page {
  background: #ffffff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1200px;
  margin: 30px auto;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 130px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 2rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }
  }
}

.summary-section {
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 1.5rem;
  padding: 1.5rem;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #eee;

  .summary-card {
    display: flex;
    flex-direction: column;
    align-items: center;

    .label {
      font-size: 0.8rem;
      font-weight: 700;
      color: #636e72;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-bottom: 4px;
    }

    .value {
      font-size: 1.4rem;
      font-weight: 800;
    }
  }
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  border: 1px solid #dee2e6;
  background: white;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 12px 15px;
    box-sizing: border-box;
    font-size: 0.95rem;
  }

  thead {
    background: #2c3e50 !important;

    th {
      color: white !important;
      font-weight: 800;
      text-transform: uppercase;
      font-size: 0.9rem;
      letter-spacing: 0.5px;
    }
  }

  tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }
}

@page {
  size: A4 portrait;
  margin: 0;
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
  }

  .student-email-report {
    background: white !important;
    padding: 0 !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 1.2cm 0.8cm !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .ledger-style-table {
    font-size: 9pt !important;
    width: 100% !important;

    th,
    td {
      padding: 6px 10px !important;
      border: 1px solid #000 !important;
      color: #000 !important;
    }

    thead th {
      background: #f1f2f6 !important;
      color: #000 !important;
      font-weight: 800 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }

  .report-header-modern {
    margin-bottom: 25px !important;
    padding-bottom: 15px !important;
    border-bottom: 4px solid #000 !important;

    .school-logo-img {
      height: 140px !important;
      max-width: 350px !important;
      object-fit: contain !important;
    }

    .school-name-full {
      font-size: 1.8rem !important;
    }
  }

  .summary-section {
    margin: 5px 0 !important;
    padding: 8px !important;
    border: 1px solid #000 !important;
    display: flex !important;
    flex-direction: row !important;
    justify-content: space-around !important;

    .summary-card {
      .label {
        font-size: 0.7rem !important;
      }

      .value {
        font-size: 1rem !important;
      }
    }
  }
}
</style>
